import clsx from 'clsx';
import { useId } from 'react';

import type { LocaleTag } from '@/config/i18n/locale';

interface LocaleFlagProps {
  className?: string;
  locale: LocaleTag;
}

/**
 * Circle-cropped flag for a supported locale, drawn as inline SVG so it
 * renders identically on Windows and macOS webviews (emoji flags fall back
 * to plain letter pairs on Windows). Shapes are simplified to stay
 * recognizable at 16-20px.
 */
export function LocaleFlag({ className, locale }: LocaleFlagProps) {
  const clipId = useId();

  return (
    <svg
      aria-hidden
      className={clsx('shrink-0', className)}
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="10" cy="10" r="10" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>{FLAG_SHAPES[locale]}</g>
      {/* Hairline ring so light flag fields keep a defined edge */}
      <circle cx="10" cy="10" fill="none" r="9.75" stroke="#00000022" strokeWidth="0.5" />
    </svg>
  );
}

/**
 * Keyed by LocaleTag so adding a locale to @workspace/locales fails
 * compilation here until its flag is drawn — no silent fallback flag.
 */
const FLAG_SHAPES: Record<LocaleTag, React.ReactNode> = {
  'en-GB': (
    <>
      <rect fill="#012169" height="20" width="20" y="0" />
      <path d="M0 0L20 20M20 0L0 20" stroke="#FFFFFF" strokeWidth="4" />
      <path d="M0 0L20 20M20 0L0 20" stroke="#C8102E" strokeWidth="1.6" />
      <path d="M10 0V20M0 10H20" stroke="#FFFFFF" strokeWidth="6.6" />
      <path d="M10 0V20M0 10H20" stroke="#C8102E" strokeWidth="4" />
    </>
  ),
  'en-IN': (
    <>
      <rect fill="#FF9933" height="6.67" width="20" y="0" />
      <rect fill="#FFFFFF" height="6.66" width="20" y="6.67" />
      <rect fill="#138808" height="6.67" width="20" y="13.33" />
      <circle cx="10" cy="10" fill="none" r="2" stroke="#000080" strokeWidth="0.7" />
      <circle cx="10" cy="10" fill="#000080" r="0.6" />
    </>
  ),
  'en-US': (
    <>
      <rect fill="#FFFFFF" height="20" width="20" y="0" />
      {[0, 1, 2, 3].map((i) => (
        <rect fill="#B22234" height="2.86" key={i} width="20" y={i * 5.72} />
      ))}
      <rect fill="#3C3B6E" height="8.58" width="10" x="0" y="0" />
      {[2.5, 5, 7.5].flatMap((x) =>
        [2.2, 4.4, 6.6].map((y) => (
          <circle cx={x} cy={y} fill="#FFFFFF" key={`${x}-${y}`} r="0.55" />
        )),
      )}
    </>
  ),
  ja: (
    <>
      <rect fill="#FFFFFF" height="20" width="20" y="0" />
      <circle cx="10" cy="10" fill="#BC002D" r="4.5" />
    </>
  ),
};
